import { Cached } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ProgressButton, SystemMinimumDate, WO2ToggleSwitch } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import DateRangePicker from 'src/common/components/DateRangePicker';
import ItemOrdering from 'src/common/components/ItemOrdering';
import { MergeReportsToggle } from 'src/features/clients/client/reportsV2/components/MergeReportsToggle';
import ReportingParameterSelect from 'src/features/clients/client/reportsV2/components/ReportingParameterSelect';
import { UploadReportAttachment } from 'src/features/clients/client/reportsV2/components/UploadReportAttachment';
import { InitiateReportRequest, ReportAttachmentType, ReportFormat } from 'src/features/clients/client/reportsV2/store/types';
import { DateRange } from 'src/store';
import { Props } from '../../container';
import { AfslItem, BulkReportJob, ReportFileNameType, ReportJobUpdateModalForm } from '../../store/types';
import { ReportGenerationCustomNamingModal } from './ReportGenerationCustomNamingModal';
import { ReportingGenerationUpdateModal } from './ReportingGenerationUpdateModal';
import { ReportFormatOption } from 'src/features/clients/client/reportsV2/components/ReportFormatOption';
import { ReportingComponentListOptions } from 'src/features/clients/client/reportsV2/store/constants';
import { ReportingComponentList } from 'src/features/clients/client/reportsV2/components/ReportingComponentList';
import { createInitiateReportRequest, getReportsToSort } from 'src/features/clients/client/reportsV2/store/helper';

export const ReportingGenerationReports = (props: Props) => {
  const history = useHistory();
  const location = useLocation();
  const routeMatch = useRouteMatch();

  const runPollRef = useRef<NodeJS.Timeout | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState<ReportAttachmentType>(ReportAttachmentType.None);
  const [batchId, setBatchId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [templates, setTemplates] = useState<boolean>(false);
  const [fullReports, setFullReports] = useState<boolean>(false);
  const [initiatedBulk, setInitiatedBulk] = useState<boolean>(false);
  const [bulkFailed, setBulkFailed] = useState<boolean>(false);
  const [tempRunDisabled, setTempRunDisabled] = useState<boolean>(false);

  const parameters = props.reporting.reports.parameters;
  const draft = props.reporting.bulk.currentDraft;

  const defaultNamingTemplate = props.reports.parameters.mergeReports
    ? ReportFileNameType.Merged.displayName
    : ReportFileNameType.NonMerged.displayName;

  const updateReportDates = (dates: DateRange) => {
    props.setDateParameter(dates);
  };

  const fetchSavedParametersList = () => {
    const selectedAfsls: AfslItem[] = props.bulk.selectedAfsls || [];
    props.fetchReportParametersList({
      kind: 'B',
      afslId: selectedAfsls.length == 1 ? selectedAfsls.map((afsl) => afsl.afslId) : [],
    });
  };

  const handleClickIncludeLogo = () => {
    props.setIncludeLogoParameter(!parameters.includeLogo);
  };

  const handleClickIncludeCoverLetter = () => {
    props.setIncludeCoverLetterParameter(!parameters.includeCoverLetter);
    setIsMenuOpen(!parameters.includeCoverLetter);
    setCurrentReportType(ReportAttachmentType.CoverLetter);
  };

  const handleClickIncludeAppendix = () => {
    props.setIncludeAppendixParameter(!parameters.includeAppendix);
    setIsMenuOpen(!parameters.includeAppendix);
    setCurrentReportType(ReportAttachmentType.Appendix);
  };

  const reportPayload: { reportName: string; reportRequest: InitiateReportRequest } = useMemo(() => {
    const request = createInitiateReportRequest({
      isDataFromTriumph: props.isDataFromTriumph,
      parameters: parameters,
      handleClickMergeReports: () => props.setMergeReportsParameter(!parameters.mergeReports),
    });
    return { reportName: request.reportName, reportRequest: request.reportRequest };
  }, [parameters]);

  const closeUploader = () => {
    setIsMenuOpen(false);

    currentReportType === ReportAttachmentType.CoverLetter ? props.setIncludeCoverLetterParameter(false) : props.setIncludeAppendixParameter(false);
  };

  const setFile = (file: File) => {
    setIsMenuOpen(false);

    if (currentReportType === ReportAttachmentType.CoverLetter) {
      props.saveCoverPageFile(file);
    } else if (currentReportType === ReportAttachmentType.Appendix) {
      props.saveAppendixFile(file);
    }
  };

  const updateBulkReport = async (request: ReportJobUpdateModalForm) => {
    if (!draft) return;

    const updateResponse = await props.updateBulkReport({
      batchId: draft.id,
      name: request.name,
      reportFileNameTemplate: request.reportFileNameTemplate,
      addToExistingClientOrGroupIds: true,
      runImmediately: false,
      reportParameters: reportPayload.reportRequest,
    });

    const batch = updateResponse.payload as BulkReportJob;
    setBatchId(batch.id);

    props.resetBulkReporting();
  };

  const hasAnyReportSelected: boolean = useMemo(() => {
    return Object.values(parameters.selectedReports).some((report) => report.selected);
  }, [parameters.selectedReports]);

  const isRunNowDisabled: boolean = useMemo(() => {
    if (!hasAnyReportSelected || tempRunDisabled) return true;

    // check if it is only ONE client or group
    if (
      (props.bulk.selectedClientRows.length == 1 && props.bulk.selectedGroupRows.length == 0) ||
      (props.bulk.selectedClientRows.length == 0 && props.bulk.selectedGroupRows.length == 1)
    ) {
      return false;
    }
    return true;
  }, [hasAnyReportSelected, tempRunDisabled]);

  const clearGenerationInterval = () => {
    if (runPollRef.current) {
      clearInterval(runPollRef.current);
      runPollRef.current = null;
    }
    props.clearImmediateBulkReport();
  };

  const handleGoBackClick = () => {
    props.clearCurrentDraft();
  };

  const runNow = () => {
    if (isRunNowDisabled || !draft) return;
    setTempRunDisabled(true);
    setInitiatedBulk(false);
    clearGenerationInterval();

    props
      .createBulkReport({
        addToExistingClientOrGroupIds: false,
        clientOrGroupIds: [...props.bulk.selectedClientRows, ...props.bulk.selectedGroupRows],
        reportParameters: reportPayload.reportRequest,
        runImmediately: true,
        reportFileNameTemplate: defaultNamingTemplate,
        allowTerminatedClients: props.bulk.allowTerminatedClient,
      })
      .unwrap()
      .then((res) => {
        props.initiateBulkRequest({ batchId: res.id, batchRecords: [], allRecords: true }).then(() => {
          setInitiatedBulk(true);
          setTempRunDisabled(false);
        });
      });
  };

  useEffect(() => {
    props.setUseTriumphAsDataSourceParameter(!parameters.isDataFromTriumph);
  }, [props.isDataFromTriumph]);

  useEffect(() => {
    if (initiatedBulk && !runPollRef.current) {
      setBulkFailed(false);
      runPollRef.current = setInterval(() => {
        if (initiatedBulk && draft && (!props.bulk.immediateBulkReport || !props.bulk.immediateBulkReport?.key)) {
          props.getImmediateBulkReportById(draft.id);
        }
      }, 2 * 1000);
    } else if (!initiatedBulk && runPollRef.current) {
      clearGenerationInterval();
    }

    return () => {
      clearGenerationInterval();
    };
  }, [initiatedBulk]);

  useEffect(() => {
    if (props.bulk.immediateBulkReport) {
      const { status, key, downloadFileName } = props.bulk.immediateBulkReport;
      if (status === 'C' && key && downloadFileName) {
        props.downloadReport({
          key: key,
          downloadFileName: downloadFileName,
          extension: false,
        });
        clearGenerationInterval();
        setInitiatedBulk(false);
      } else if (status === 'E') {
        clearGenerationInterval();
        setBulkFailed(true);
        setInitiatedBulk(false);
      }
    }
  }, [props.bulk.immediateBulkReport]);

  useEffect(() => {
    if (batchId.length > 0) {
      history.push(`/reporting/${batchId}`);
    }
  }, [batchId]);

  useEffect(() => {
    if (!parameters.selectedParameter) return;
    props.getReportParametersById({
      id: parameters.selectedParameter?.id,
    });
  }, [parameters.selectedParameter]);

  useEffect(() => {
    fetchSavedParametersList();
    props.setInceptionDate(SystemMinimumDate);
    props.resetReports();
    props.fetchBenchmarks(-1);
  }, []);

  useEffect(() => {
    return () => {
      clearGenerationInterval();
    };
  }, []);

  return (
    <Grid container spacing={3} direction="column">
      {initiatedBulk && (
        <Grid item>
          <Alert variant="outlined" severity="info">
            <Typography>Report is currently being generated and should be ready shortly.</Typography>
          </Alert>
        </Grid>
      )}
      {bulkFailed && (
        <Grid item>
          <Alert variant="outlined" severity="error">
            <Typography>Unable to run now at this time. Please try again later.</Typography>
          </Alert>
        </Grid>
      )}
      <ItemOrdering
        itemsToSort={getReportsToSort(parameters.reportsToSort, parameters.reportFormat)}
        setSelectedReport={props.setSelectedReportOrderParameter}
        setShowItemOrderingModal={props.setShowItemOderingModalParameter}
        generateReport={() => props.setShowItemOderingModalParameter(false)}
        isOpen={parameters.showItemOrdering}
      />
      {!props.hasClientDirectAccess && (
        <Grid item container spacing={2}>
          <Grid item flex={1}>
            <ReportingParameterSelect
              disabled={props.getReportParametersByIdProgress.isLoading}
              items={props.savedParametersList}
              selectedParameter={parameters?.selectedParameter ?? null}
              setParameter={props.setSelectedReportingParameter}
            />
          </Grid>
        </Grid>
      )}
      {!props.hasClientDirectAccess && (
        <Grid item>
          <Divider />
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Card elevation={0}>
            <CardContent>
              <Grid item>
                <Typography variant="h3" align="left" style={{ padding: '10px' }}>
                  Report Parameters
                </Typography>
              </Grid>
              <Grid container spacing={2} direction="column">
                <Grid item>
                  <DateRangePicker dateRange={parameters.dates} setDateRange={updateReportDates} inceptionDate={parameters.inceptionDate} />
                </Grid>
                <Grid item>
                  <MergeReportsToggle
                    mergeReports={parameters.mergeReports}
                    setMergeReportsParameter={props.setMergeReportsParameter}
                    setShowItemOderingModalParameter={props.setShowItemOderingModalParameter}
                    disabled={parameters.reportsToSort.length < 2 || parameters.reportFormat === ReportFormat.JSON}
                  />
                </Grid>
                <Grid item container>
                  <WO2ToggleSwitch
                    checked={parameters.includeLogo}
                    onChange={handleClickIncludeLogo}
                    color="primary"
                    name="includeLogo"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                  <Typography variant="h4" style={{ paddingTop: '10px' }}>
                    Include Logo
                  </Typography>
                </Grid>
                <Grid item container>
                  <WO2ToggleSwitch
                    checked={parameters.includeCoverLetter}
                    onChange={handleClickIncludeCoverLetter}
                    color="primary"
                    name="includeCoverPage"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
                  />
                  <Typography variant="h4" style={{ paddingTop: '10px' }}>
                    Cover Page
                  </Typography>
                </Grid>
                <Grid item container>
                  <WO2ToggleSwitch
                    checked={parameters.includeAppendix}
                    onChange={handleClickIncludeAppendix}
                    color="primary"
                    name="includeAppendix"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
                  />
                  <Typography variant="h4" style={{ paddingTop: '10px' }}>
                    Appendix
                  </Typography>
                </Grid>
                <Grid item>
                  <WO2Button variant="contained" onClick={() => setTemplates(true)}>
                    Set naming template
                  </WO2Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item container display="flex" direction="column" xs={9} mt={2} spacing={1}>
          <Grid item container spacing={2}>
            <Grid item>
              <WO2Button variant="contained" onClick={handleGoBackClick}>
                Back
              </WO2Button>
            </Grid>
            <Grid item>
              <WO2Button variant="contained" onClick={props.expandAll}>
                Expand
              </WO2Button>
            </Grid>
            <Grid item>
              <ReportFormatOption
                format={parameters.reportFormat}
                setReportFormat={props.setReportFormat}
                hasClientEditAdminOnlyPermission={props.hasClientEditAdminOnlyPermission}
                isDisplayOnly={false}
              />
            </Grid>
            {props.hasClientEditAdminOnlyPermission && (
              <Grid item>
                <Box display="flex" alignItems="center">
                  <WO2ToggleSwitch checked={fullReports} onChange={(e) => setFullReports(e.target.checked)} color="primary" />
                  <Typography variant="h4">Full Reports</Typography>
                </Box>
              </Grid>
            )}
            <Grid item flex={1} />

            <Grid item>
              <ProgressButton
                progress={props.updateBulkReportProgress}
                startIcon={Cached}
                disabled={isRunNowDisabled}
                style={{ borderRadius: 24, height: 48, width: 132, marginRight: 12 }}
                color={'primary'}
                onClick={() => runNow()}
                variant="contained"
              >
                Run Now
              </ProgressButton>

              <LoadingButton
                loading={props.updateBulkReportProgress.isLoading}
                disabled={!hasAnyReportSelected}
                variant="contained"
                onClick={() => setSaving(true)}
              >
                Save
              </LoadingButton>
            </Grid>
          </Grid>

          <ReportingComponentList
            history={history}
            location={location}
            match={routeMatch}
            {...props}
            reportingList={ReportingComponentListOptions}
            fullReports={fullReports}
            allowConsolidation
          />
        </Grid>
      </Grid>
      <UploadReportAttachment isOpen={isMenuOpen} handleImport={setFile} handleClose={closeUploader} title={`Upload ${currentReportType}`} />
      {saving && (
        <ReportingGenerationUpdateModal
          updateBulkReport={updateBulkReport}
          isOpen={saving}
          namingTemplate={props.bulk.namingTemplate}
          isMergeReports={props.reports.parameters.mergeReports}
          handleClose={() => setSaving(false)}
        />
      )}
      {templates && (
        <ReportGenerationCustomNamingModal
          clearNamingTemplate={props.clearNamingTemplate}
          appendNamingTemplate={props.appendNamingTemplate}
          swapNamingTemplateTags={props.swapNamingTemplateTags}
          setNamingTemplateTagValue={props.setNamingTemplateTagValue}
          namingTemplate={props.bulk.namingTemplate}
          deleteNamingTemplateTag={props.deleteNamingTemplateTag}
          isOpen={templates}
          handleClose={() => setTemplates(false)}
        />
      )}
    </Grid>
  );
};
